import { useEffect, useMemo, useState } from 'react';
import { hoursToMilliseconds, isFuture, millisecondsToHours } from 'date-fns';
import { isAddress } from 'viem';
import { useAccount } from 'wagmi';
import CodeBlock from '@/shared/components/atoms/CodeBlock';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import WalletActionButton from '@/shared/components/WalletActionButton';
import {
  useExecuteRedemption,
  usePendingRedemption,
  useRedemptionDelay,
} from '@/shared/hooks/funding';
import useAccountInfo from '@/shared/hooks/useAccountInfo';
import useSteps from '@/shared/hooks/useSteps';
import useToast from '@/shared/hooks/useToast';
import CodeIcon from '@/shared/icons/flip-ui-kit/large/Code';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import TokenAmount from '@/shared/utils/TokenAmount';
import { StartView, ThresholdSignatureView, FinalizeView } from './Views';

const SubmitExtrinsic = ({
  redeemAmount,
  flipBalance,
  address,
}: {
  redeemAmount: TokenAmount | undefined;
  flipBalance: TokenAmount | null;
  address: string;
}) => {
  if (!redeemAmount || !flipBalance) return null;

  const amount = redeemAmount.eq(flipBalance) ? '' : `--exact ${redeemAmount.toFixed()} `;

  const command = `chainflip-cli -c /path/to/Default.toml redeem ${amount}${address}`;

  return (
    <div className="flex w-full flex-col gap-y-6">
      <div className="flex flex-col gap-y-2">
        <span className="flex items-center gap-x-1 text-cf-light-2">
          <CodeIcon className="text-cf-blue-2" />
          Validator Command
        </span>
        <CodeBlock code={command} />
      </div>
      <Callout type="info">
        You must execute this command in your Validator terminal.{' '}
        <span className="font-aeonikBold">Remember to change the path of the config file.</span>
      </Callout>
    </div>
  );
};

const RedemptionModal = ({
  open,
  onClose,
  accountId,
}: {
  open: boolean;
  onClose: VoidFunction;
  accountId: `cF${string}` | undefined;
}) => {
  const evmAccount = useAccount();
  const { step, stepId, stepCount, advanceStep, resetSteps, gotoStep } = useSteps(
    'Redeeming Funds',
    'Submit Extrinsic',
    'Waiting for request to be registered...',
    'Finalize Withdrawal',
  );

  const [refetchInterval, setRefetchInterval] = useState<number>();

  const { data: accountInfo = null } = useAccountInfo(accountId, {
    select: (account) => (account.role === 'validator' ? account : null),
  });

  const { data: redemption = null, refetch } = usePendingRedemption(accountId, {
    refetchInterval,
  });

  const flipBalance = useMemo(
    () => TokenAmount.fromAsset(accountInfo?.flip_balance, 'Flip'),
    [accountInfo?.flip_balance],
  );
  const redeemableBalance = useMemo(
    () => TokenAmount.fromAsset(accountInfo?.estimated_redeemable_balance ?? 0n, 'Flip'),
    [accountInfo?.estimated_redeemable_balance],
  );

  const [address, setAddress] = useState(evmAccount.address ?? '');
  const [redeemAmount, setRedeemAmount] = useState<TokenAmount>();
  const [tokenError, setTokenError] = useState<string>();
  const [addressError, setAddressError] = useState<string>();

  const { data: delay = hoursToMilliseconds(48) } = useRedemptionDelay();

  useEffect(() => {
    if (open && redemption) gotoStep('Finalize Withdrawal');
  }, [open, redemption]);

  const toast = useToast('validator-redemption-modal');
  const {
    mutate: execute,
    isPending,
    reset: resetExecute,
    isSuccess,
  } = useExecuteRedemption({
    onError: (err) =>
      toast.error('Failed to execute withdrawal', { description: getViemError(err) }),
    onSuccess: () => {
      toast.success('Withdrawal executed successfully');
      onClose();
    },
  });

  useEffect(() => {
    if (isSuccess) {
      if (redemption) {
        setRefetchInterval(5_000);
      } else {
        resetExecute();
        setRefetchInterval(undefined);
      }
    } else if (redemption) {
      setRefetchInterval(undefined);
    } else if (step === 'Waiting for request to be registered...') {
      setRefetchInterval(5_000);
    }
  }, [step, redemption, isSuccess]);

  const reset = () => {
    setAddress(evmAccount.address ?? '');
    setRedeemAmount(undefined);
    setTokenError(undefined);
    setAddressError(undefined);
    setRefetchInterval(undefined);
    resetSteps();
    refetch();
  };

  let onClick;
  let buttonText;
  let iconPos;
  let description;
  let disabled;
  let requireConnection = false;

  if (step === 'Redeeming Funds') {
    onClick = () => {
      if (!redeemableBalance) return;
      if (!isAddress(address)) {
        setAddressError('Please enter a valid address');
        return;
      }
      if (!redeemAmount) {
        setTokenError('Please enter an amount to redeem');
        return;
      }
      if (redeemAmount.gt(redeemableBalance)) return;
      setTokenError(undefined);
      advanceStep();
    };
    buttonText = 'Continue';
    iconPos = 'right' as const;
  } else if (step === 'Submit Extrinsic') {
    description = (
      <>
        Log into your Chainflip node and execute the above command.{' '}
        <span className="font-aeonikBold text-cf-white">
          Remember to change the path to the config file, which is specific to your system.
        </span>
      </>
    );
    buttonText = 'Confirm';
    onClick = advanceStep;
  } else if (step === 'Waiting for request to be registered...') {
    description =
      'The State Chain is submitting your redemption request to the State Chain Gateway. This should usually take a minute or two, but in some circumstances might take longer.';
    disabled = redemption === null;
    // buttonText = 'Continue';
  } else if (step === 'Finalize Withdrawal') {
    buttonText = 'Execute withdrawal';
    onClick = () => {
      if (!redemption) return;
      if (isFuture(redemption.startTime)) {
        toast.error('The withdrawal is not ready to be executed');
        return;
      }
      toast.loading('Executing withdrawal...');
      execute(accountId!);
    };
    requireConnection = true;
    const time = redemption ? new Date(redemption.expiryTime).toLocaleString() : 'expiration time';
    const messageStart =
      redemption && isFuture(redemption?.startTime)
        ? `In order to finalise your withdraw, you must wait ${millisecondsToHours(delay)} hours. This is to limit the amount of funds at risk in Chainflip's vaults.`
        : `Your ${FLIP_SYMBOL} is ready to withdraw!`;
    description = `${messageStart} Failure to finalise the process by ${time} will result in your ${FLIP_SYMBOL} being re-credited to your Validator balance. You will be required to go through the withdraw process again in order to receive it.`;
  }

  return (
    <DashboardModalWithCloseButton onClose={onClose} open={open} afterLeave={reset}>
      <ModalContent
        step={{ current: stepId, max: stepCount }}
        description={description}
        title={step}
        titleSize="large"
        buttons={
          buttonText ? (
            <WalletActionButton
              chainId={chainflipAssetMap.Flip.chain.id}
              requireConnection={requireConnection}
              fullWidth
              iconPos={iconPos}
              onClick={onClick}
              disabled={disabled || 'ifLoading'}
              loading={isPending}
            >
              {buttonText}
            </WalletActionButton>
          ) : undefined
        }
      >
        {step === 'Redeeming Funds' && (
          <StartView
            tokenError={tokenError}
            addressError={addressError}
            address={address}
            setAddress={setAddress}
            redeemAmount={redeemAmount}
            setRedeemAmount={setRedeemAmount}
            flipBalance={flipBalance}
            redeemableBalance={redeemableBalance}
            type="validator"
          />
        )}
        {step === 'Submit Extrinsic' && (
          <SubmitExtrinsic
            redeemAmount={redeemAmount}
            flipBalance={flipBalance}
            address={address}
          />
        )}
        {step === 'Waiting for request to be registered...' && <ThresholdSignatureView />}
        {step === 'Finalize Withdrawal' && <FinalizeView redemption={redemption!} />}
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
};

export default RedemptionModal;
